// components
import Column from '@drivesally/sally-ui/lib/components/Grid/Column/Column';
import Modal from '@drivesally/sally-ui/lib/components/Modal/Modal';
import Row from '@drivesally/sally-ui/lib/components/Grid/Row/Row';
import Switch from '@drivesally/sally-ui/lib/components/Switch/Switch';
import Table from '@drivesally/sally-ui/lib/components/Table/Table';

// layout components
import CardLoader from '../../../layout/components/CardLoader/CardLoader';

// legacy components
import {AttributeInput} from '../../../components/components/atoms/AttributeInput';

// lib
import displayTimeZoneSpecificDate from '../../../libs/displayTimeZoneSpecificDate';

// propTypes
import PropTypes from 'prop-types';

// react
import React, {Fragment} from 'react';

// type
import amountType from '@drivesally/sally-fn/lib/types/types/amount.type';
import dateTimeType from '@drivesally/sally-fn/lib/types/types/dateTime.type';

const chargeColumns = [
  {
    key: 'date_created',
    label: 'Created',
  },
  {
    key: 'description',
    label: 'Description',
    span: 2,
  },
  {
    key: 'amount',
    label: 'Amount',
  },
  {
    key: 'balance',
    label: 'Balance',
  },
];

const PaymentPlanEditorModal = ({
  branch,
  charges = [],
  disable_app_payment_extensions,
  dollarAmount,
  estimatedPayment,
  loading,
  onChange,
  onChangeAppPaymentExtensions,
  onClose,
  onSave,
  payment_plan_dollar_amount,
  payment_plan_percent_amount,
  paymentPlanBalance,
  saving,
  showAppPaymentExtensionOption,
  visible,
}) => {
  const disableAppPaymentExtensions = disable_app_payment_extensions.api.format();
  const tableCharges = charges.filter((charge) => Number(charge.balance) !== 0);
  const hasPaymentPlanCharges = !!tableCharges.length;
  return (
    <Modal
      visible={visible}
      title="Payment Plan"
      onClose={onClose}
      size="medium"
      buttonsRight={
        !hasPaymentPlanCharges || disableAppPaymentExtensions
          ? []
          : [
              {
                label: 'Save',
                loading: saving,
                disabled: loading,
                onClick: onSave,
              },
            ]
      }
    >
      {(Content) =>
        loading ? (
          <Content padding="none">
            <CardLoader />
          </Content>
        ) : !hasPaymentPlanCharges ? (
          <Content>
            <Row>
              <Column>Please assign charges to a payment plan</Column>
            </Row>
          </Content>
        ) : (
          <Fragment>
            <Content padding="none">
              <Table
                columns={chargeColumns}
                infinite={false}
                noBorder
                noRadius
                smallNoResultsLabel
              >
                {(TableRow) =>
                  tableCharges.map((charge) => (
                    <TableRow key={charge.id}>
                      {(TableColumn) => [
                        <TableColumn key="date_created">
                          {displayTimeZoneSpecificDate({
                            date: charge.date_created,
                            type: dateTimeType,
                            branch,
                          })}
                        </TableColumn>,
                        <TableColumn key="description" span={2}>
                          {charge.description || '-'}
                        </TableColumn>,
                        <TableColumn key="amount">
                          ${amountType(charge.amount || 0).format()}
                        </TableColumn>,
                        <TableColumn key="balance">
                          ${amountType(charge.balance || 0).format()}
                        </TableColumn>,
                      ]}
                    </TableRow>
                  ))
                }
              </Table>
            </Content>
            <Content>
              <Row margin>
                <Column>
                  Payment Plan Balance:{' '}
                  <b>${amountType(paymentPlanBalance).format()}</b>
                </Column>
              </Row>
              {showAppPaymentExtensionOption && (
                <Row margin={!disableAppPaymentExtensions}>
                  <Column>
                    <AttributeInput
                      value={disable_app_payment_extensions}
                      onChange={onChangeAppPaymentExtensions}
                      disabled={saving}
                    >
                      {disable_app_payment_extensions.label.default}
                    </AttributeInput>
                  </Column>
                </Row>
              )}
              {(!showAppPaymentExtensionOption ||
                !disableAppPaymentExtensions) && (
                <Fragment>
                  <Row margin>
                    <Column>
                      <Switch
                        value={dollarAmount}
                        onChange={(value) => onChange(value, 'dollarAmount')}
                        disabled={saving}
                      >
                        Dollar amount
                      </Switch>
                    </Column>
                  </Row>
                  <Row margin={!dollarAmount}>
                    <Column>
                      {dollarAmount ? (
                        <AttributeInput
                          value={payment_plan_dollar_amount}
                          onChange={onChange}
                          disabled={saving}
                        >
                          {payment_plan_dollar_amount.label.default}
                        </AttributeInput>
                      ) : (
                        <AttributeInput
                          value={payment_plan_percent_amount}
                          onChange={onChange}
                          disabled={saving}
                        >
                          {payment_plan_percent_amount.label.default}
                        </AttributeInput>
                      )}
                    </Column>
                  </Row>
                  {!dollarAmount && (
                    <Row>
                      <Column>
                        Estimated weekly payment: $
                        {amountType(estimatedPayment).format()}
                      </Column>
                    </Row>
                  )}
                </Fragment>
              )}
            </Content>
          </Fragment>
        )
      }
    </Modal>
  );
};

PaymentPlanEditorModal.propTypes = {
  branch: PropTypes.object,
  charges: PropTypes.array,
  dollarAmount: PropTypes.bool,
  loading: PropTypes.bool,
  onChange: PropTypes.func,
  onChangeAppPaymentExtensions: PropTypes.func,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  payment_plan_dollar_amount: PropTypes.object,
  payment_plan_percent_amount: PropTypes.object,
  saving: PropTypes.bool,
  showAppPaymentExtensionOption: PropTypes.bool,
  visible: PropTypes.bool,
};

export default PaymentPlanEditorModal;
